// Use-case landing pages (/use-cases/:slug): who Bumpups is for, each shown on a real public video with
// the chapters and a chat answer Bumpups actually produced for it (example ids from
// myWorkspace/onboarding/examples.js; chapters in exampleChapters.json).
import {
  faGraduationCap, faChalkboardUser, faFutbol, faPodcast, faBriefcase, faComments,
} from '@fortawesome/free-solid-svg-icons';

export const USE_CASE_PAGES = [
  {
    slug: 'lecture-videos',
    label: 'Lectures & studying',
    icon: faGraduationCap,
    summary: 'Study guides, notes and quizzes from lecture recordings, with the minute each idea comes up.',
    seo: {
      title: 'AI lecture notes & study guides from lecture videos | Bumpups',
      description: 'Upload a lecture recording or paste a YouTube link. Bumpups writes timestamped chapters, study guides and quizzes, and answers questions about the lecture. Free to start.',
    },
    h1: 'Turn any lecture video into notes you can study from',
    lead: 'Upload a class recording or paste a YouTube lecture. Bump AI watches all of it, slides included, then writes chapters, study guides and quizzes that point to the exact minute.',
    example: {
      id: 'neural-networks',
      question: 'Make a short study guide with timestamps: the key ideas, in order.',
      answer: `Here is a short study guide of the key ideas covered in the video, in order:

* **Neurons and activations (2:43):** a neuron is a container holding a number between 0 and 1, its "activation". The first layer's 784 neurons are the grayscale values of the 28×28 pixels.
* **Network structure (3:45):** an input layer, "hidden layers" and an output layer. The 10 output neurons stand for the digits 0–9; their activation is the network's confidence.
* **Why layers (5:32):** early layers pick out edges, later layers combine them into loops and lines, and the last layer uses those patterns to name the digit.
* **Weights (8:51):** each connection has a weight, and a neuron takes the weighted sum of the previous layer's activations.`,
    },
    prompts: [
      'Make a study guide with timestamps', 'Quiz me with 10 questions', 'Explain the hardest part simply',
      'What will probably be on the exam?', 'Turn this into flashcards', 'Which minute covers the proof?',
    ],
    steps: [
      ['Add the lecture', 'Upload the recording (MP4, MOV or WebM) or paste the YouTube link.'],
      ['Get the chapters', 'Bump AI breaks the lecture into chapters with times, so you can jump to any topic.'],
      ['Study with it', 'Ask for a study guide, a quiz or a simpler explanation. Every answer cites the minute.'],
    ],
    faq: [
      ['Does it read the slides?', 'Yes. For uploads and longer videos Bump AI also describes what is on screen, so slides, diagrams and code count too.'],
      ['How long can a lecture be?', 'Free covers YouTube videos up to your 60 monthly minutes, and your first upload gets a free preview of its first 30 minutes. Pro handles lectures up to 3 hours 30 minutes.'],
      ['Can I ask about a whole course?', 'Yes. Ask your videos answers one question across every lecture you have added, and points to the lecture and minute.'],
      ['Can I download the notes?', 'Yes. Export the chapters, the transcript, subtitles or your chats from any workspace.'],
    ],
  },
  {
    slug: 'teachers',
    label: 'Teachers & lesson videos',
    icon: faChalkboardUser,
    summary: 'Find the moments that matter in a lesson recording, with timestamps for reflections and evidence.',
    seo: {
      title: 'Lesson video analysis for teachers, with timestamps | Bumpups',
      description: 'Upload a lesson recording and find every moment you checked for understanding, gave feedback or used a strategy, with timestamps for reflections, coaching and edTPA commentary.',
    },
    h1: 'Find the moments that matter in your lesson recordings',
    lead: 'Upload a lesson video and ask what you need: where you checked for understanding, which strategies you used, how students responded. Every answer comes with the minute, ready for a reflection or commentary.',
    example: {
      id: 'classroom-lesson',
      question: 'Find the moments where the teacher checks for understanding, with timestamps.',
      answer: `Here are the moments from the video where the teacher checks for understanding:

* **02:08** The teacher asks, "What do I mean distinguish?" to make sure the class understands the key vocabulary in the learning objective, and calls on a student who answers correctly.
* **03:36** After students find the word "scraggly" in their book, she asks what it means there, checking their understanding of the word in context.
* **04:01** She writes "The duck is scraggly" on the board and asks, "Is that a fact or an opinion?", checking that they can apply the concept to an example.
* **07:07** After a group activity, the class votes on a statement and a student is asked to defend the answer: "Why?"`,
    },
    prompts: [
      'Find where I checked for understanding', 'List the teaching strategies I used, with timestamps',
      'How much of the lesson did students talk?', 'What would make this lesson stronger?',
      'Pull evidence for my reflection, with times', 'Summarize the lesson for my mentor',
    ],
    steps: [
      ['Upload the lesson', 'Add the recording from your phone or camera. Your first upload is a free preview.'],
      ['Get the timeline', 'Bump AI chapters the lesson, from the opener to the exit ticket.'],
      ['Ask for evidence', 'Find the exact moments for your reflection, observation notes or commentary.'],
    ],
    faq: [
      ['Can it help with edTPA commentary?', 'It finds and timestamps the moments you want to write about, so you can cite them. The commentary itself should be your own, as your program requires.'],
      ['Who can see my lesson video?', 'Only you. Your uploads and workspaces are private to your account, and you can delete them at any time.'],
      ['Does it understand what happens on screen?', 'Yes. Uploads are transcribed and the visuals are described too, so board work and student work count.'],
      ['Can I download what it finds?', 'Yes. Export the chapters, transcript or your chats as text or Markdown.'],
    ],
  },
  {
    slug: 'coaches',
    label: 'Coaches & game film',
    icon: faFutbol,
    summary: 'Break down game film and practice videos into coaching points, with the moment for each one.',
    seo: {
      title: 'Game film breakdown for coaches: AI timestamps & notes | Bumpups',
      description: 'Upload game film or a practice video. Bumpups chapters it and turns it into coaching points, drills and player feedback, each with a timestamp. Free to start.',
    },
    h1: 'Break down game film in minutes, not hours',
    lead: 'Upload a match, a practice or a tactics video. Bump AI watches it, chapters it, and turns it into coaching points with the moment for each one, so your players know exactly what to rewatch.',
    example: {
      id: 'build-up-play',
      question: 'Break down the key tactical ideas with timestamps, as coaching points for my players.',
      answer: `Here are the key tactical ideas from the video, as coaching points for your players:

**1. The build-up structure (4-2-2-2)**
* **Use the goalkeeper:** we build from the back in a 4-2-2-2 shape, and the goalkeeper is an active player against heavy pressure (0:46).
* **Stagger the midfield:** central midfielders never stand on the same line. One drops deeper to form a triangle with the center-backs. Look for the "third man" (1:05).
* **Have a purpose:** the aim of the build-up is to attack the final third as quickly as possible (3:34).

**2. "Fishing" for the press**
* **Bait the opponent:** center-backs, wait for the pressure to come before you release the ball (2:11).`,
    },
    prompts: [
      'Break down the key tactical ideas with timestamps', 'List every scoring chance and when it happened',
      'Turn this into a training session plan', 'What should our defenders work on?',
      'Write feedback for the number 9', 'Summarize the first half',
    ],
    steps: [
      ['Upload the film', 'Add the match or practice video, up to 3 hours 30 minutes on Pro.'],
      ['Get the moments', 'Bump AI chapters it, from kickoff to set pieces, so you can jump straight to them.'],
      ['Coach from it', 'Ask for coaching points, drills or player feedback, each linked to its moment.'],
    ],
    faq: [
      ['Which sports does it work for?', 'Any. It reads what is said and describes what is shown, so commentary, coaching audio and the action itself all count.'],
      ['Can I use full-length matches?', 'Yes. Pro uploads videos up to 3 hours 30 minutes and 16 GB. Your first upload gets a free preview of its first 30 minutes.'],
      ['Can I compare several games?', 'Yes. Ask your videos answers one question across all your film, like "where did we concede from set pieces?"'],
      ['Can I share notes with players?', 'Export the chapters or the chat as text and send them with the video.'],
    ],
  },
  {
    slug: 'youtube-creators',
    label: 'YouTube creators & podcasters',
    icon: faPodcast,
    summary: 'Chapters, show notes, descriptions and clip ideas for every episode or video.',
    seo: {
      title: 'YouTube chapters, show notes & clip ideas for creators | Bumpups',
      description: 'Get YouTube-ready timestamps, show notes, descriptions and quotable clip moments for every video or podcast episode. Paste a link or upload the file. Free to start.',
    },
    h1: 'Chapters, show notes and clips for every episode',
    lead: 'Paste your YouTube link or upload the episode. Bump AI writes chapters that YouTube accepts, then show notes, descriptions and the moments worth clipping, all with timestamps.',
    example: {
      id: 'procrastination',
      question: 'Pull 5 quotable moments with timestamps that would make good short clips.',
      answer: `Here are quotable moments from the video that would make great short clips:

* **The Instant Gratification Monkey (5:04–5:15):** "He lives entirely in the present moment. He has no memory of the past, no knowledge of the future, and he only cares about two things: easy and fun."
* **The Dark Playground (6:44–6:56):** "It's where leisure activities happen at times when leisure activities are not supposed to be happening."
* **The Panic Monster (7:35–7:52):** "He suddenly wakes up anytime a deadline gets too close or there's danger of public embarrassment… and he's the only thing that the monkey is terrified of."`,
    },
    prompts: [
      'Write a YouTube description with chapters', 'Pull 5 quotable moments for shorts', 'Write show notes',
      'Suggest 3 titles for this episode', 'What did the guest say about pricing?', 'Write a newsletter recap',
    ],
    steps: [
      ['Add the episode', 'Paste the YouTube link or upload the file before you publish.'],
      ['Copy the chapters', 'Timestamps start at 0:00 and follow YouTube’s rules, so they show up as chapters.'],
      ['Repurpose it', 'Ask for show notes, a description, clip ideas or a newsletter, in 11 languages.'],
    ],
    faq: [
      ['Will YouTube show my chapters?', 'Bumpups chapters start at 0:00, keep each chapter at least 10 seconds long and stay inside the video, which is what YouTube needs. Check any list with our free chapter checker.'],
      ['Can I get the transcript?', 'Yes. Download it as text or as SRT or VTT subtitles from the Export menu.'],
      ['Can I publish to YouTube from Bumpups?', 'Pro includes Creator Studio, which uploads and schedules videos to your channel with their timestamps.'],
      ['Does it work for podcasts?', 'Yes, for any video podcast on YouTube or any episode file you upload.'],
    ],
  },
  {
    slug: 'meeting-recordings',
    label: 'Meetings & webinars',
    icon: faBriefcase,
    summary: 'Decisions, action items and summaries from recorded meetings, with the minute they were said.',
    seo: {
      title: 'Meeting recording summaries, decisions & action items | Bumpups',
      description: 'Upload a Zoom, Teams or Meet recording, or a webinar. Bumpups lists the decisions and action items, writes the summary and answers questions, with timestamps. Free to start.',
    },
    h1: 'Get the decisions and action items from any meeting recording',
    lead: 'Upload a Zoom, Teams or Google Meet recording, a webinar or a training session. Bump AI lists what was decided, who owns what, and the minute each thing was said.',
    example: {
      id: 'team-meeting',
      question: 'List the decisions and action items, with timestamps.',
      answer: `Here are the decisions and action items from the meeting:

**Decisions**
* **4:09** For internal projects, the code suggestions feature moves to a Google model instead of the team's own.
* **15:38** The team is moving to a single Semgrep-based analyzer, replacing the various open-source analyzers used before.

**Action items**
* **4:16** All leaders: encourage teams to test the new code suggestions on internal projects and give feedback.
* **6:04** The director: confirm with HR whether managers can require 360 reviews and growth plans, or if they stay opt-in.
* **14:22** The analyzer team: reduce false positives with the new rules, starting with benchmarking and a way for users to report them.`,
    },
    prompts: [
      'List the decisions and action items', 'Summarize this for someone who missed it', 'Draft a follow-up email',
      'What did the client say about the timeline?', 'What questions came up, and the answers?', 'Write the meeting minutes',
    ],
    steps: [
      ['Upload the recording', 'Any MP4, MOV or WebM from Zoom, Teams, Meet or a webinar tool.'],
      ['Get the outline', 'Bump AI chapters the meeting by topic, so you can jump to the part you need.'],
      ['Ask what you need', 'Decisions, owners, a summary or a follow-up email, each tied to the minute.'],
    ],
    faq: [
      ['Does it join my meetings?', 'No. Bumpups works on recordings you upload after the meeting, so nothing joins your calls.'],
      ['Can I search across many meetings?', 'Yes. Ask your videos answers questions like "when did we agree on the budget?" across every recording, and cites the meeting and minute.'],
      ['Who can see my recordings?', 'Only you. Workspaces are private to your account, and you can delete them at any time.'],
      ['Can I get a transcript?', 'Yes. Export the transcript as text, or as SRT or VTT subtitles.'],
    ],
  },
  {
    slug: 'interviews',
    label: 'Interviews & research',
    icon: faComments,
    summary: 'Every answer, quote and theme from interview recordings, with timestamps to cite.',
    seo: {
      title: 'Interview video analysis: answers, quotes & themes | Bumpups',
      description: 'Upload interview recordings for research, hiring or journalism. Bumpups summarizes every answer, pulls quotes and finds themes, each with a timestamp. Free to start.',
    },
    h1: 'Every answer, quote and theme from your interview recordings',
    lead: 'Upload user interviews, candidate interviews or recorded conversations. Bump AI summarizes each answer, pulls exact quotes and finds the themes across interviews, all with timestamps you can cite.',
    example: {
      id: 'job-interview',
      question: 'Summarize each answer the candidate gave, with timestamps, and how strong it was.',
      answer: `Here is a summary of the candidate's answers, with how strong each one is:

* **0:07 Tell me about yourself:** finishing a master's in education and relocating for his wife's job. *Moderate:* honest about the move, but it leads with his personal life rather than his skills.
* **0:34 How did you hear about the position?** He wanted a career that matched his passion for the outdoors and working with people, and searched for this company specifically. *Strong:* it shows deliberate intent.
* **1:20 What do you know about our company?** Its founding story, its values and its place on a "best companies to work for" list. *Very strong:* clear evidence of research.`,
    },
    prompts: [
      'Summarize each answer with timestamps', 'Pull exact quotes about pricing', 'What themes come up most?',
      'Where did the participant hesitate?', 'Compare these two candidates', 'Write a one-page interview summary',
    ],
    steps: [
      ['Upload the interviews', 'Add each recording as its own workspace.'],
      ['Get the structure', 'Bump AI chapters each interview by question, so every answer is one click away.'],
      ['Analyze across them', 'Pull quotes, compare answers and find themes across every interview with Ask your videos.'],
    ],
    faq: [
      ['Are the quotes exact?', 'Answers come from the transcript and cite the minute, so you can check every quote in the video.'],
      ['Can I find themes across interviews?', 'Yes. Ask your videos looks across all your recordings and cites which interview and minute each point comes from.'],
      ['Who can see the recordings?', 'Only you. Workspaces are private to your account, and you can delete them at any time.'],
      ['Can I export transcripts for coding?', 'Yes. Download transcripts as text, SRT or VTT, or everything as Markdown.'],
    ],
  },
];

export const useCasePage = slug => USE_CASE_PAGES.find(p => p.slug === slug) || null;
